"use client";

import React from "react";
import { ConfigProvider } from "antd";
import type { CSSProperties } from "react";
import type { ThemeConfig } from "antd";
import {
  BookingThemeRoot,
  buildBookingCssVars,
} from "@openings/template-components";

import {
  resolveBookingVisualVariant,
  resolveBookingVisualVariantKey,
} from "./bookingVisualVariants";
import type {
  BookingVisualVariantKey,
  BookingVisualVariantTokens,
} from "./bookingVisualVariants";
import { BookingSkinTokensContext } from "./bookingSkinContext";
import { claimActiveBookingVariant } from "./activeBookingVariant";
import { BOOKING_AMBIENT_CLASS, buildSkinMotionCss } from "./bookingSkinMotion";

/**
 * Re-seats chrome that the flat presets render with its own defaults (the
 * @openings/ui phone input, Ant's Divider). Emitted for skins only, so
 * adding a skin cannot change an existing preset.
 */
const SKIN_CHROME_CSS = `
  /* The @openings/ui phone input carries inline light styles (white
     field, black-alpha ink, #d9d9d9 border, #000 accent). Re-seat it on
     the skin tokens wherever a booking theme or its portal is in scope;
     !important is required to beat the inline styles. Fallbacks mirror
     the component's own values. */
  .openings-editorial-booking-theme .oe-phone-input,
  .openings-booking-portal .oe-phone-input {
    /* The component sets this var INLINE (accentColor prop, default
       #000000), so the skin re-seat needs !important to win. */
    --oe-phone-accent: var(--openings-color-primary, #000000) !important;
  }

  .openings-editorial-booking-theme .oe-phone-input__input,
  .openings-booking-portal .oe-phone-input__input {
    background: var(--openings-booking-variant-surface-color, #ffffff) !important;
    color: var(--openings-color-text, rgba(0, 0, 0, 0.88)) !important;
    border-radius: var(--openings-booking-control-radius, 8px) !important;
  }

  /* Resting border matches the sibling Ant inputs (locally-resolved
     --openings-color-border). RESTING ONLY: hover/focus must fall through
     to the component's own accent rules (--oe-phone-accent, re-seated on
     the skin primary above) so the input darkens like every other
     control, and the error state must stay red. */
  .openings-editorial-booking-theme
    .oe-phone-input:not(.oe-phone-input--error)
    .oe-phone-input__input:not(:focus):not(:hover),
  .openings-booking-portal
    .oe-phone-input:not(.oe-phone-input--error)
    .oe-phone-input__input:not(:focus):not(:hover) {
    border-color: var(--openings-color-border, #d9d9d9) !important;
  }

  .openings-editorial-booking-theme .oe-phone-input__input--large,
  .openings-booking-portal .oe-phone-input__input--large {
    height: var(--openings-booking-control-height, 40px) !important;
  }

  .openings-editorial-booking-theme .oe-phone-input__input--middle,
  .openings-booking-portal .oe-phone-input__input--middle {
    height: var(--openings-booking-time-button-height, 32px) !important;
  }

  .openings-editorial-booking-theme .oe-phone-input__input::placeholder,
  .openings-booking-portal .oe-phone-input__input::placeholder {
    color: color-mix(
      in srgb,
      var(--openings-color-text, #000000) 25%,
      transparent
    );
  }

  .openings-editorial-booking-theme .oe-phone-input__label,
  .openings-booking-portal .oe-phone-input__label {
    color: var(--openings-color-text, rgba(0, 0, 0, 0.88));
  }

  .openings-editorial-booking-theme .oe-phone-input__globe svg,
  .openings-booking-portal .oe-phone-input__globe svg {
    color: var(--openings-color-text-secondary, #8c8c8c);
  }

  .openings-editorial-booking-theme .oe-phone-input__globe:hover,
  .openings-booking-portal .oe-phone-input__globe:hover {
    background: var(--openings-color-surface-hover, rgba(0, 0, 0, 0.04));
  }

  .openings-editorial-booking-theme .oe-phone-input__globe--active svg,
  .openings-booking-portal .oe-phone-input__globe--active svg {
    color: var(--openings-color-primary, #000000);
  }

  /* Ant's Divider ignores both the colorSplit token and the base seeds in
     this build and keeps its light #d9d9d9 line. Deterministic override:
     the skin's divider color, falling back to the Ant default wherever no
     variant root is present. */
  /* Overlay titles carry the skin's heading face, like the card titles. */
  .openings-editorial-booking-theme .ant-modal-title,
  .openings-editorial-booking-theme .ant-drawer-title,
  .openings-booking-portal .ant-modal-title,
  .openings-booking-portal .ant-drawer-title {
    font-family: var(--openings-booking-heading-font-family, inherit);
  }

  .openings-editorial-booking-theme .ant-divider,
  .openings-booking-portal .ant-divider {
    border-color: var(--openings-booking-variant-divider-color, #d9d9d9) !important;
  }

`;

const EDITORIAL_BOOKING_VARS = `
  .openings-editorial-booking-theme,
  .openings-booking-portal {
    --openings-booking-brand-font-size:
      var(--openings-booking-variant-brand-font-size);
    --openings-booking-card-title-font-size:
      var(--openings-booking-variant-card-title-font-size);
    --openings-booking-editorial-card-padding:
      var(--openings-booking-variant-card-padding);
    --openings-booking-editorial-outer-padding:
      var(--openings-booking-variant-outer-padding);
    --openings-booking-profile-title-font-size:
      var(--openings-booking-variant-profile-title-font-size);
    --openings-booking-card-title-line-height: 1.25;
    --openings-booking-page-title-line-height: 1.3;
    --openings-booking-section-line-height: 1.35;
    --openings-booking-member-name-line-height: 1.3;
    --openings-booking-meta-line-height: 1.35;
    --openings-booking-member-header-align: flex-start;
    --openings-booking-time-grid-columns: 4;
    --openings-booking-time-grid-gap:
      var(--openings-booking-variant-time-grid-gap, 8px);
    --availability-green: var(--openings-booking-status-positive);
    --openings-booking-status-dot-size: 7px;
    --openings-booking-status-dot-gap: 6px;
    --openings-booking-divider-color:
      var(--openings-booking-variant-divider-color);
    --openings-color-surface-hover:
      var(--openings-booking-variant-surface-hover-color);
    font-family: var(--openings-booking-body-font-family);
  }

  .openings-editorial-booking-theme :where(a, button):focus-visible {
    outline: 2px solid var(--openings-color-primary, #111);
    outline-offset: 2px;
  }

  .openings-editorial-booking-theme
    :where(
      .openings-time-btn,
      .openings-location-change-button,
      .openings-member-location-change-button,
      .openings-member-info-button,
      .booking-date-input button
    ):focus-visible {
    outline-color: var(--openings-color-primary, #111) !important;
  }

  .openings-editorial-booking-theme
    :where(
      .openings-location-change-button,
      .openings-member-location-change-button
    ) {
    color: var(--openings-color-primary, #111) !important;
  }

  .openings-editorial-booking-theme
    .booking-date-input
    > .ant-btn:not(:disabled),
  .openings-editorial-booking-theme
    .booking-service-select
    .ant-select-suffix {
    color: var(--openings-color-primary, #111) !important;
  }

  .openings-editorial-booking-theme
    .booking-date-input
    > .ant-btn:disabled {
    color: var(--openings-color-text-secondary, #666) !important;
  }

  .openings-editorial-booking-theme .openings-member-info-button {
    align-items: flex-start !important;
    justify-content: flex-end !important;
    padding-top: 2px !important;
  }

  /* Glyph is corner-pinned above, so the focus ring wraps the glyph (see
     ScheduleBooking) — suppress the generic button ring and recolour. */
  .openings-editorial-booking-theme .openings-member-info-button:focus-visible {
    outline: none !important;
  }
  .openings-editorial-booking-theme
    .openings-member-info-button:focus-visible
    > svg {
    outline-color: var(--openings-color-primary, #111);
  }

  .openings-editorial-booking-theme .openings-services-booking-center {
    max-width: none;
  }

  @media (max-width: 1105px) {
    .openings-editorial-booking-theme {
      --openings-booking-time-grid-columns: 3;
    }
  }

  @media (max-width: 767px) {
    .openings-editorial-booking-theme .openings-time-grid {
      --openings-booking-time-grid-columns: 4;
      --openings-booking-time-button-padding-x: 6px;
    }

    .openings-editorial-booking-theme
      .openings-time-grid
      > .openings-time-btn {
      min-width: 0;
    }
  }

  @media (max-width: 359px) {
    .openings-editorial-booking-theme .openings-time-grid {
      --openings-booking-time-grid-gap: 4px;
      --openings-booking-time-button-padding-x: 2px;
    }
  }

  @media (max-width: 991px) {
    .openings-editorial-booking-theme,
    .openings-booking-portal {
      --openings-booking-brand-font-size:
        var(--openings-booking-variant-brand-font-size-stacked);
      --openings-booking-editorial-card-padding:
        var(--openings-booking-variant-card-padding-stacked);
      --openings-booking-editorial-outer-padding:
        var(--openings-booking-variant-outer-padding-stacked);
      --openings-booking-profile-title-font-size:
        var(--openings-booking-variant-profile-title-font-size-stacked);
    }
  }
`;

type EditorialBookingPageRootProps = {
  children: React.ReactNode;
  className?: string;
  visualVariant?: BookingVisualVariantKey;
};

type BookingVisualVariantCssProperties = CSSProperties & {
  [key: `--openings-booking-${string}`]: string | number;
};

function buildVariantCssProperties(
  tokens: BookingVisualVariantTokens,
): BookingVisualVariantCssProperties {
  return {
    "--openings-booking-variant-brand-font-size": `${tokens.brandFontSize}px`,
    "--openings-booking-variant-brand-font-size-stacked": `${tokens.brandFontSizeStacked}px`,
    "--openings-booking-brand-font-weight": tokens.brandFontWeight,
    "--openings-booking-variant-card-title-font-size": `${tokens.cardTitleFontSize}px`,
    "--openings-booking-card-title-font-weight": tokens.cardTitleFontWeight,
    "--openings-booking-variant-profile-title-font-size": `${tokens.profileTitleFontSize}px`,
    "--openings-booking-variant-profile-title-font-size-stacked": `${tokens.profileTitleFontSizeStacked}px`,
    "--openings-booking-variant-outer-padding": `${tokens.outerPadding}px`,
    "--openings-booking-variant-outer-padding-stacked": `${tokens.outerPaddingStacked}px`,
    "--openings-booking-variant-card-padding": `${tokens.cardPadding}px`,
    "--openings-booking-variant-card-padding-stacked": `${tokens.cardPaddingStacked}px`,
    "--openings-booking-control-height": `${tokens.controlHeight}px`,
    "--openings-booking-control-label-gap": `${tokens.controlLabelGap}px`,
    "--openings-booking-control-radius": `${tokens.controlRadius}px`,
    "--openings-booking-time-button-height": `${tokens.timeButtonHeight}px`,
    "--openings-booking-time-button-padding-x": `${tokens.timeButtonPaddingX}px`,
    "--openings-booking-time-button-font-size": `${tokens.timeButtonFontSize}px`,
    "--openings-booking-time-button-font-weight":
      tokens.timeButtonFontWeight,
    "--openings-booking-variant-time-grid-gap": `${tokens.timeGridGap}px`,
    "--openings-booking-time-button-radius": `${tokens.timeButtonRadius}px`,
    "--openings-booking-section-font-size": `${tokens.sectionFontSize}px`,
    "--openings-booking-section-font-weight": tokens.sectionFontWeight,
    "--openings-booking-section-text-transform":
      tokens.sectionTextTransform,
    "--openings-booking-section-letter-spacing":
      tokens.sectionLetterSpacing,
    "--openings-booking-section-text-color": tokens.sectionTextColor,
    "--openings-booking-status-positive": tokens.availabilityColor,
    "--openings-booking-variant-divider-color": tokens.dividerColor,
    "--openings-booking-variant-surface-hover-color": tokens.surfaceHoverColor,
    "--openings-booking-header-info-border-color": tokens.borderColor,
    "--openings-booking-body-font-family": tokens.bodyFontFamily,
    "--openings-booking-heading-font-family": tokens.headingFontFamily,
    "--openings-booking-canvas-background": tokens.canvasBackground,
    "--openings-booking-surface-shadow": tokens.surfaceShadow,
    "--openings-booking-variant-surface-color": tokens.surfaceColor,
    "--openings-booking-variant-text-color": tokens.textColor,
    "--openings-booking-muted-chip": tokens.mutedChipColor,
    "--openings-booking-control-border": tokens.controlBorderColor,
    "--openings-booking-control-hover-border":
      tokens.controlHoverBorderColor,
    "--openings-booking-time-hover-border":
      tokens.timeButtonHoverBorderColor,
    "--openings-booking-time-selected-bg":
      tokens.timeButtonSelectedBackground,
    "--openings-booking-time-selected-border":
      tokens.timeButtonSelectedBorderColor,
    "--openings-booking-time-selected-text":
      tokens.timeButtonSelectedTextColor,
    "--openings-booking-time-selected-shadow":
      tokens.timeButtonSelectedShadow,
  };
}

/** `#RRGGBB` at an alpha; non-hex inputs pass through unchanged. */
function inkAtAlpha(hex: string, alpha: number): string {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Ant's status washes (Alert backgrounds and borders) are fixed light
 * tints, not derived from colorBgBase: on a dark skin the manage page's
 * "Your appointment has been canceled" Alert painted the skin's light ink
 * on #f6ffed. Dark skins tint their own surface instead.
 */
function darkStatusWashes(): ThemeConfig["token"] {
  const wash = (hex: string) => ({
    bg: inkAtAlpha(hex, 0.12),
    border: inkAtAlpha(hex, 0.45),
  });
  const success = wash("#52C41A");
  const info = wash("#1677FF");
  const warning = wash("#FAAD14");
  const error = wash("#FF4D4F");
  return {
    colorSuccessBg: success.bg,
    colorSuccessBorder: success.border,
    colorInfoBg: info.bg,
    colorInfoBorder: info.border,
    colorWarningBg: warning.bg,
    colorWarningBorder: warning.border,
    colorErrorBg: error.bg,
    colorErrorBorder: error.border,
  };
}

function buildBookingAntTheme(
  tokens: BookingVisualVariantTokens,
  skin: boolean,
): ThemeConfig {
  // Skins only: the flat presets keep Ant's own black-on-white derived
  // scale, exactly as before skins existed, so adding a skin cannot shift
  // an existing preset's labels, icons or disabled states.
  const skinSeeds: ThemeConfig["token"] = skin
    ? {
        // Seed Ant's DERIVED scale from the skin's ink and surface.
        // Without these, every derived alpha color (modal close icon,
        // disabled button text/fill, form-item labels, placeholders) is
        // computed from black-on-white and disappears on a dark skin.
        colorTextBase: tokens.textColor,
        colorBgBase: tokens.surfaceColor,
        // Not derived from the base seeds in this antd build — each of
        // these stays at its light-theme default on a dark skin: the modal
        // close X (colorIcon), the Divider/drawer-header hairline
        // (colorSplit, default #d9d9d9), and Typography type="secondary"
        // (colorTextDescription, default rgba(0,0,0,0.45)).
        colorIcon: inkAtAlpha(tokens.textColor, 0.45),
        colorIconHover: inkAtAlpha(tokens.textColor, 0.88),
        colorSplit: tokens.dividerColor,
        colorTextDescription: tokens.textSecondaryColor,
        // The root layout's tenantAntdTheme pins these explicitly (hover
        // and link ink #333/#111, tertiary text black-alpha), so they never
        // re-derive from a nested colorPrimary: on a dark skin a hovered
        // default button turned #333 on near-black. Skins pin their own.
        colorPrimaryHover: tokens.accentColor,
        colorLink: tokens.accentColor,
        colorLinkHover: tokens.accentColor,
        colorLinkActive: tokens.accentColor,
        colorTextTertiary: tokens.textSecondaryColor,
        colorBorderSecondary: tokens.dividerColor,
        colorBgLayout: tokens.canvasColor,
        ...(tokens.colorScheme === "dark" ? darkStatusWashes() : {}),
      }
    : {};
  return {
    token: {
      ...skinSeeds,
      colorPrimary: tokens.accentColor,
      colorPrimaryText: tokens.accentTextColor,
      colorBgContainer: tokens.surfaceColor,
      colorBgElevated: tokens.surfaceColor,
      colorBorder: tokens.borderColor,
      colorText: tokens.textColor,
      colorTextSecondary: tokens.textSecondaryColor,
      controlHeight: tokens.timeButtonHeight,
      controlHeightLG: tokens.controlHeight,
      fontFamily: tokens.bodyFontFamily,
      fontSize: tokens.nameFontSize,
      fontSizeLG: tokens.nameFontSize,
      borderRadius: tokens.controlRadius,
      borderRadiusLG: tokens.controlRadius,
    },
    components: {
      // Filled primary buttons and checked boxes paint their ink from
      // colorTextLightSolid / colorWhite, not colorPrimaryText. Both are
      // white, which is right for the dark-accent presets and unreadable on
      // a bright accent (Hacker's green), so follow the skin's on-accent ink.
      Button: {
        primaryColor: tokens.accentTextColor,
        // Skins only: Ant's filled danger button (the manage page's "Yes,
        // Cancel Appointment") is white on #ff4d4f, 3.27:1. Dark ink on the
        // same red reads 6.9:1 on every skin.
        ...(skin ? { dangerColor: "#1F0A0A" } : {}),
      },
      Checkbox: {
        colorWhite: tokens.accentTextColor,
      },
      // Skins only: Ant's initials avatar paints white on its placeholder
      // grey (under 2:1). A skin seats it on the muted chip in its ink.
      ...(skin
        ? {
            Avatar: {
              colorTextPlaceholder: tokens.mutedChipColor,
              colorTextLightSolid: tokens.textColor,
            },
          }
        : {}),
      Select: {
        // Follows accentColor on the flat presets (via the deriver); a skin
        // may soften it. The outline ring stays accent-derived because the
        // hex-alpha suffix needs a hex color.
        hoverBorderColor: tokens.controlHoverBorderColor,
        activeBorderColor: tokens.controlHoverBorderColor,
        activeOutlineColor: `${tokens.accentColor}29`,
        optionSelectedBg: tokens.surfaceHoverColor,
        optionActiveBg: tokens.surfaceHoverColor,
      },
    },
  };
}

/**
 * The booking theme for an overlay mounted outside `EditorialBookingPageRoot`
 * (the root layout's appointment modal, which quote links open): the same
 * Ant theme and skin tokens as the root on screen. The CSS variables reach
 * it through the `openings-booking-portal` class, which the root re-scopes
 * for skins. With no root mounted, `visualVariant` is null and the overlay
 * keeps the layout's theme. The tree shape never changes, so a variant
 * arriving later cannot remount an open flow.
 */
export function BookingOverlayTheme({
  visualVariant,
  children,
}: {
  visualVariant: BookingVisualVariantKey | null;
  children: React.ReactNode;
}) {
  const variant = visualVariant
    ? resolveBookingVisualVariant(visualVariant)
    : null;
  return (
    <ConfigProvider
      theme={
        variant ? buildBookingAntTheme(variant.tokens, variant.skin) : undefined
      }
    >
      <BookingSkinTokensContext.Provider
        value={variant?.skin ? variant.tokens : null}
      >
        {children}
      </BookingSkinTokensContext.Provider>
    </ConfigProvider>
  );
}

/**
 * Shared visual frame for standalone booking surfaces.
 *
 * Keeping the neutral canvas, booking tokens, and Ant control overrides here
 * prevents the booking home and member-detail route from drifting apart.
 */
export function EditorialBookingPageRoot({
  children,
  className,
  visualVariant,
}: EditorialBookingPageRootProps) {
  const resolvedVariantKey =
    resolveBookingVisualVariantKey(visualVariant);
  const resolvedVariant =
    resolveBookingVisualVariant(resolvedVariantKey);

  // Overlays mounted outside this tree (the layout's appointment modal)
  // follow it through BookingOverlayTheme.
  React.useEffect(
    () => claimActiveBookingVariant(resolvedVariantKey),
    [resolvedVariantKey],
  );
  const variantStyles = buildVariantCssProperties(
    resolvedVariant.tokens,
  );
  const classes = [
    "openings-live-booking-theme",
    "openings-editorial-booking-theme",
    `openings-booking-visual-variant-${resolvedVariantKey}`,
    resolvedVariant.motion?.ambient ? BOOKING_AMBIENT_CLASS : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Overlays that portal to document.body (the desktop appointment Modal
  // tags itself `openings-booking-portal`) sit outside the inline vars on
  // the themed root, so every `--openings-*` var would fall back to its
  // default there. Re-scope the resolved theme + variant vars to the portal
  // class; custom properties only, so nothing else about the portal is
  // touched. Skins only: the flat presets' overlays keep the fallbacks
  // they rendered with before skins existed.
  const portalVarsCss = resolvedVariant.skin ? `.openings-booking-portal {\n  color-scheme: ${
    resolvedVariant.tokens.colorScheme
  };\n${Object.entries({
    ...buildBookingCssVars(resolvedVariant.theme),
    ...variantStyles,
  })
    .filter(([key]) => key.startsWith("--"))
    .map(([key, value]) => `  ${key}: ${value};`)
    .join("\n")}\n}` : "";

  // color-scheme: dark only switches the UA to its OWN dark chrome, whose
  // grey scrollbar thumb and resize grip still glare on a near-black skin.
  // Dark skins therefore get explicit scrollbar/resizer styling from their
  // tokens. Emitted ONLY for dark schemes so every light preset keeps the
  // native chrome untouched.
  const darkUaChromeCss =
    resolvedVariant.tokens.colorScheme === "dark"
      ? `
  .openings-editorial-booking-theme *,
  .openings-booking-portal * {
    scrollbar-width: thin;
    scrollbar-color: var(--openings-booking-control-border) transparent;
  }

  .openings-editorial-booking-theme *::-webkit-scrollbar,
  .openings-booking-portal *::-webkit-scrollbar {
    width: 10px;
    height: 10px;
    background: transparent;
  }

  .openings-editorial-booking-theme *::-webkit-scrollbar-thumb,
  .openings-booking-portal *::-webkit-scrollbar-thumb {
    background: var(--openings-booking-control-border);
    border-radius: 5px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }

  .openings-editorial-booking-theme *::-webkit-scrollbar-thumb:hover,
  .openings-booking-portal *::-webkit-scrollbar-thumb:hover {
    background: var(--openings-booking-control-hover-border);
    border: 2px solid transparent;
    background-clip: padding-box;
  }

  .openings-editorial-booking-theme *::-webkit-scrollbar-track,
  .openings-editorial-booking-theme *::-webkit-scrollbar-corner,
  .openings-booking-portal *::-webkit-scrollbar-track,
  .openings-booking-portal *::-webkit-scrollbar-corner {
    background: transparent;
  }

  .openings-editorial-booking-theme *::-webkit-resizer,
  .openings-booking-portal *::-webkit-resizer {
    background: transparent;
  }

  /* Error text: the light-page red (#b91c1c) is unreadable on near-black. */
  .openings-editorial-booking-theme,
  .openings-booking-portal {
    --openings-booking-skin-error: #f87171;
  }

  /* A business logo is usually dark ink on transparency and would vanish
     on a dark canvas; dark skins seat it on a light plate. */
  .openings-editorial-booking-theme .openings-booking-brand-logo {
    box-sizing: content-box;
    padding: 4px 8px;
    border-radius: var(--openings-booking-control-radius, 6px);
    background: #ffffff;
  }
`
      : "";

  // Surfaces the flat presets render with pinned values (the booking
  // center's B&W, the info panel hairline, the consultation list, the
  // portaled calendar) read --openings-booking-skin-* / -calendar-skin-*
  // vars with those pinned values as fallbacks. Only a skin defines them.
  const skinCalendarCss = resolvedVariant.skin
    ? `
  .openings-editorial-booking-theme,
  .openings-booking-portal {
    --openings-booking-skin-surface:
      var(--openings-booking-variant-surface-color);
    --openings-booking-skin-text: var(--openings-booking-variant-text-color);
    --openings-booking-skin-control-border:
      var(--openings-booking-control-border);
    --openings-booking-skin-divider:
      var(--openings-booking-variant-divider-color);
    --openings-booking-skin-control-height:
      var(--openings-booking-control-height);
    --openings-booking-skin-touch-target: 40px;
    --openings-booking-skin-section-text-color:
      var(--openings-booking-section-text-color);
    --openings-booking-skin-text-secondary:
      var(--openings-color-text-secondary);
    --openings-booking-skin-primary: var(--openings-color-primary);
    --openings-booking-skin-canvas: var(--openings-booking-canvas-background);
    --openings-booking-skin-font-family:
      var(--openings-booking-body-font-family);
    --openings-booking-skin-heading-font-family:
      var(--openings-booking-heading-font-family);
    --openings-booking-skin-primary-text: var(--openings-color-primary-text);
    /* Unrated feedback stars: the light-page grey is 1.3:1 on a tinted
       canvas. */
    --openings-booking-skin-rating-empty:
      var(--openings-color-text-secondary);
    --openings-booking-calendar-skin-surface: var(--openings-color-surface);
    --openings-booking-calendar-skin-text: var(--openings-color-text);
    --openings-booking-calendar-skin-text-secondary:
      var(--openings-color-text-secondary);
    --openings-booking-calendar-skin-border: var(--openings-color-border);
    --openings-booking-calendar-skin-header:
      var(--openings-color-surface-hover);
    --openings-booking-calendar-skin-hover:
      var(--openings-color-surface-hover);
    --openings-booking-calendar-skin-selected-background:
      var(--openings-color-primary);
    --openings-booking-calendar-skin-selected-text:
      var(--openings-color-primary-text);
    --openings-booking-calendar-skin-disabled:
      var(--openings-color-text-secondary);
    --openings-booking-calendar-skin-radius: var(--openings-booking-radius);
    --openings-booking-calendar-skin-control-radius:
      var(--openings-booking-control-radius);
    --openings-booking-calendar-skin-shadow:
      var(--openings-booking-surface-shadow);
    --openings-booking-calendar-skin-font-family:
      var(--openings-booking-body-font-family);
  }

  .openings-booking-calendar--neutral .react-datepicker__day {
    border-radius: var(--openings-booking-control-radius);
  }

  .openings-booking-calendar--neutral .react-datepicker__current-month {
    font-family: var(--openings-booking-heading-font-family);
  }

  /* 40px touch-target floor for the business-info drawer close. */
  .openings-booking-info-drawer .ant-drawer-close {
    width: 40px;
    min-width: 40px;
    height: 40px;
  }
`
    : "";

  // Only a skin with motion (Sunset, Pop, Aurora) emits any of it.
  const motionCss = buildSkinMotionCss(resolvedVariant.motion);

  return (
    <ConfigProvider
      theme={buildBookingAntTheme(
        resolvedVariant.tokens,
        resolvedVariant.skin,
      )}
    >
      <style>{EDITORIAL_BOOKING_VARS}</style>
      {portalVarsCss ? <style>{portalVarsCss}</style> : null}
      {darkUaChromeCss ? <style>{darkUaChromeCss}</style> : null}
      {resolvedVariant.skin ? <style>{SKIN_CHROME_CSS}</style> : null}
      {skinCalendarCss ? <style>{skinCalendarCss}</style> : null}
      {motionCss ? <style>{motionCss}</style> : null}
      <BookingThemeRoot
        theme={resolvedVariant.theme}
        className={classes}
        data-booking-visual-variant={resolvedVariantKey}
        style={{
          ...variantStyles,
          minHeight: "100dvh",
          overflowX: "clip",
          colorScheme: resolvedVariant.tokens.colorScheme,
          background:
            "var(--openings-booking-canvas-background, var(--openings-color-background, #F6F6F6))",
        }}
      >
        <BookingSkinTokensContext.Provider
          value={resolvedVariant.skin ? resolvedVariant.tokens : null}
        >
          {children}
        </BookingSkinTokensContext.Provider>
      </BookingThemeRoot>
    </ConfigProvider>
  );
}
